
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Line, System, Employee, StationAssignment, UserStatus, ShiftAssignment } from '../types';
import StationRow from './StationRow';
import HelpIcon from './HelpIcon';
import { useAppContext } from '../contexts/AppContext';
import { useInterface } from '../contexts/InterfaceContext';

interface ProductionStatusData {
  currentShift: {
    number: number;
    isActive: boolean;
  };
}


type StationShiftTab = 'SHIFT_1' | 'SHIFT_2';

const StationsPageDB: React.FC = () => {
  const [lines, setLines] = useState<Line[]>([]);
  const [systems, setSystems] = useState<System[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [stationAssignments, setStationAssignments] = useState<StationAssignment[]>([]);
  const [activeSystemId, setActiveSystemId] = useState<string | null>(null);
  const [activeLineId, setActiveLineId] = useState<string | null>(null);
  const [activeShiftTab, setActiveShiftTab] = useState<StationShiftTab>('SHIFT_1');
  const [isLoading, setIsLoading] = useState(true);
  const [currentShift, setCurrentShift] = useState<number | null>(null);
  const [lineMode, setLineMode] = useState<string>('stop');
  const { isAppInitialized, apiUrl } = useAppContext();
  const { selectedLineId } = useInterface();

  // Effect for initial data load and storage event listening
  useEffect(() => {
    if (!isAppInitialized) {
      console.log("StationsPageDB: App not initialized, deferring data load.");
      setIsLoading(true); // Keep loading true until app is initialized
      return;
    }

    const initialLoad = async () => {
      console.log("StationsPageDB: Initial data load running.");
      
      // Fetch employees from API with assignment information
      try {
        const employeesResponse = await fetch(`${apiUrl}/employees?includeAssignments=true`);
        if (employeesResponse.ok) {
          const employeesResult = await employeesResponse.json();
          const employeesData = Array.isArray(employeesResult) ? employeesResult : (employeesResult.data || []);
          setEmployees(employeesData);
          console.log("StationsPageDB: Loaded employees from database:", employeesData.length);
        }
      } catch (error) {
        console.error("StationsPageDB: Error fetching employees:", error);
      }

      // Fetch systems from API
      try {
        console.log("StationsPageDB: Fetching systems from database...");
        const systemsResponse = await fetch(`${apiUrl}/systems`);
        if (systemsResponse.ok) {
          const systemsResult = await systemsResponse.json();
          // Handle both wrapped {data: [...]} and direct array responses
          const systemsData = Array.isArray(systemsResult) ? systemsResult : (systemsResult.data || []);
          setSystems(systemsData);
          console.log("StationsPageDB: Loaded systems from database:", systemsData.length);
          
          // Set first visible system as active if available
          const visibleSystemsData = systemsData.filter(system => Boolean(system.showOnStations));
          if (visibleSystemsData.length > 0 && !activeSystemId) {
            setActiveSystemId(visibleSystemsData[0].id);
          }
        } else {
          console.error("StationsPageDB: Failed to fetch systems");
        }
      } catch (error) {
        console.error("StationsPageDB: Error fetching systems:", error);
      }

      // Fetch lines from API
      try {
        console.log("StationsPageDB: Fetching lines from database...");
        const linesResponse = await fetch(`${apiUrl}/lines`);
        if (linesResponse.ok) {
          const linesResult = await linesResponse.json();
          // Handle both wrapped {data: [...]} and direct array responses
          const linesData = Array.isArray(linesResult) ? linesResult : (linesResult.data || []);
          setLines(linesData);
          console.log("StationsPageDB: Loaded lines from database:", linesData.length);
        } else {
          console.error("StationsPageDB: Failed to fetch lines");
        }
      } catch (error) {
        console.error("StationsPageDB: Error fetching lines:", error);
      }

      // Note: System mode will be fetched per line in a separate effect

      // Load station assignments from database
      try {
        console.log("StationsPageDB: Fetching station assignments from database...");
        const response = await fetch(`${apiUrl}/station-assignments`);
        console.log("StationsPageDB: Database response status:", response.status);
        
        if (response.ok) {
          const result = await response.json();
          console.log("StationsPageDB: Database response:", result);
          
          // Handle both wrapped and unwrapped responses
          const data = result.data || result;
          if (data && Array.isArray(data)) {
            setStationAssignments(data);
            console.log("StationsPageDB: Loaded station assignments from database:", data.length);
            console.log("StationsPageDB: First assignment example:", data[0]);
          } else {
            console.log("StationsPageDB: Invalid data format from database:", data);
            setStationAssignments([]);
          }
        } else {
          console.log("StationsPageDB: Failed to load station assignments");
          setStationAssignments([]);
        }
      } catch (error) {
        console.error("StationsPageDB: Error loading station assignments from database:", error);
        setStationAssignments([]);
      }
      
      setIsLoading(false);
      console.log("StationsPageDB: Data load complete");
    };

    initialLoad();
  }, [isAppInitialized, apiUrl]);

  // Effect to fetch line-specific mode when active line changes
  useEffect(() => {
    if (!isAppInitialized || !activeLineId) return;

    const fetchLineMode = async () => {
      try {
        const response = await fetch(`${apiUrl}/line-modes`);
        const result = await response.json();
        
        if (result.message === 'success' && result.data) {
          const lineModeData = result.data.find((lm: any) => lm.line_id === activeLineId);
          if (lineModeData) {
            setLineMode(lineModeData.current_mode || 'stop');
            console.log(`StationsPageDB: Line ${activeLineId} mode is: ${lineModeData.current_mode}`);
          } else {
            setLineMode('stop');
            console.log(`StationsPageDB: No mode found for line ${activeLineId}, defaulting to stop`);
          }
        }
      } catch (error) {
        console.error('StationsPageDB: Error fetching line mode:', error);
        setSystemMode('stop');
      }
    };

    fetchLineMode();
    
    // Set up interval to refresh line mode every second
    const interval = setInterval(fetchLineMode, 1000);
    
    return () => clearInterval(interval);
  }, [isAppInitialized, activeLineId, apiUrl]);

  // Effect for managing activeLineId based on lines
  useEffect(() => {
    console.log("StationsPageDB: ActiveLineId effect running. Lines count:", lines.length, "Current activeLineId:", activeLineId, "Dashboard selectedLineId:", selectedLineId);
    if (lines.length > 0) {
      const currentActiveLineIsValid = lines.some(line => line.id === activeLineId);
      
      // If no active line is set, prefer the dashboard's selected line
      if (!activeLineId) {
        // Check if the dashboard's selected line exists in our lines
        const dashboardLineExists = selectedLineId && lines.some(line => line.id === selectedLineId);
        if (dashboardLineExists) {
          console.log("StationsPageDB: Setting activeLineId to dashboard selected line:", selectedLineId);
          setActiveLineId(selectedLineId);
        } else {
          console.log("StationsPageDB: Setting activeLineId to first line:", lines[0].id);
          setActiveLineId(lines[0].id);
        }
      } else if (!currentActiveLineIsValid) {
        // Current active line is invalid, try dashboard's selected line first
        const dashboardLineExists = selectedLineId && lines.some(line => line.id === selectedLineId);
        if (dashboardLineExists) {
          console.log("StationsPageDB: Current line invalid, setting to dashboard selected line:", selectedLineId);
          setActiveLineId(selectedLineId);
        } else {
          console.log("StationsPageDB: Current line invalid, setting to first line:", lines[0].id);
          setActiveLineId(lines[0].id);
        }
      }
    } else if (lines.length === 0 && activeLineId !== null) {
        console.log("StationsPageDB: No lines available, setting activeLineId to null.");
        setActiveLineId(null);
    }
  }, [lines, activeLineId, selectedLineId]);
  
  // Effect to re-fetch employees when active line changes to ensure we have latest assignment data
  useEffect(() => {
    if (!isAppInitialized || !activeLineId) return;
    
    const fetchEmployeesWithAssignments = async () => {
      try {
        console.log("StationsPageDB: Re-fetching employees with assignments for line change");
        const employeesResponse = await fetch(`${apiUrl}/employees?includeAssignments=true`);
        if (employeesResponse.ok) {
          const employeesResult = await employeesResponse.json();
          const employeesData = Array.isArray(employeesResult) ? employeesResult : (employeesResult.data || []);
          setEmployees(employeesData);
          console.log("StationsPageDB: Re-fetched employees with assignments:", employeesData.length);
          
          // Log first few employees with assignments for debugging
          const withAssignments = employeesData.filter(e => e.currentAssignment);
          console.log("StationsPageDB: Employees with assignments:", withAssignments.length, "First example:", withAssignments[0]);
        }
      } catch (error) {
        console.error("StationsPageDB: Error re-fetching employees:", error);
      }
    };
    
    fetchEmployeesWithAssignments();
  }, [activeLineId, isAppInitialized, apiUrl]);

  // Effect for initializing and cleaning up station assignments
  useEffect(() => {
     if (!isAppInitialized || isLoading || lines.length === 0) { 
        console.log("StationsPageDB: Skipping station assignment init. isLoading:", isLoading, "Lines count:", lines.length);
        return;
    }
    console.log("StationsPageDB: Station assignment effect running. Lines count:", lines.length);

    const initializeAssignments = async () => {
      const currentAssignments = [...stationAssignments];
      let updatedAssignments: StationAssignment[] = [...currentAssignments];
      let assignmentsChanged = false;
      const validAssignmentIds = new Set<string>();
      const newAssignments: StationAssignment[] = [];

      lines.forEach(line => {
        // Create assignments for both shifts
        ['SHIFT_1', 'SHIFT_2'].forEach(shift => {
          for (let i = 1; i <= line.numberOfStations; i++) {
            // Check if assignment exists by lineId, stationNumber, and shift
            const existingAssignment = updatedAssignments.find(sa => 
              sa.lineId === line.id && 
              sa.stationNumber === i && 
              sa.shift === (shift as ShiftAssignment)
            );
            
            if (existingAssignment) {
              // Mark existing assignment as valid
              validAssignmentIds.add(existingAssignment.id);
            } else {
              // Generate a unique ID for new assignment
              const assignmentId = `${line.id}-station-${i}-${shift}`;
              const newAssignment = {
                id: assignmentId,
                lineId: line.id,
                stationNumber: i,
                assignedEmployeeId: null,
                status: UserStatus.Inactive, // Default: Not Working
                stationStatus: 'active' as 'active' | 'disabled', // Default: Active
                shift: shift as ShiftAssignment,
              };
              updatedAssignments.push(newAssignment);
              newAssignments.push(newAssignment);
              validAssignmentIds.add(assignmentId);
              assignmentsChanged = true;
              console.log("StationsPageDB: Created new assignment for", assignmentId);
            }
          }
        });
      });
      
      // Keep only assignments that belong to current lines and stations
      const cleanedAssignments = updatedAssignments.filter(sa => {
        const line = lines.find(l => l.id === sa.lineId);
        if (!line) return false;
        if (sa.stationNumber > line.numberOfStations) return false;
        return true;
      });
      if (cleanedAssignments.length !== updatedAssignments.length) {
          assignmentsChanged = true;
          console.log("StationsPageDB: Cleaned up stale assignments.");
      }
      
      // Save new assignments to database
      if (newAssignments.length > 0) {
        try {
          for (const assignment of newAssignments) {
            // Don't send status when creating assignments - let the backend preserve existing status
            const { status, ...assignmentWithoutStatus } = assignment;
            const response = await fetch(`${apiUrl}/station-assignments`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(assignmentWithoutStatus),
            });

            if (!response.ok) {
              console.error(`Failed to create station assignment ${assignment.id}:`, response.statusText);
            } else {
              console.log(`StationsPageDB: Successfully created station assignment ${assignment.id} in database`);
            }
          }
        } catch (error) {
          console.error('StationsPageDB: Error creating station assignments in database:', error);
        }
      }
      
      if (assignmentsChanged) {
          setStationAssignments(cleanedAssignments);
          console.log("StationsPageDB: Station assignments updated. Total:", cleanedAssignments.length);
      }
    };

    initializeAssignments();
  }, [lines, isLoading, isAppInitialized, apiUrl]);

  // Effect to fetch current shift and set default tab
  useEffect(() => {
    if (!isAppInitialized) return;

    const fetchCurrentShift = async () => {
      try {
        const response = await fetch(`${apiUrl}/production-status`);
        const result = await response.json();
        
        if (result.message === 'success' && result.data?.currentShift) {
          const shiftNumber = result.data.currentShift.number;
          const isActive = result.data.currentShift.isActive;
          
          setCurrentShift(shiftNumber);
          
          // Only set default tab if shift is active and it's a valid shift number
          if (isActive && (shiftNumber === 1 || shiftNumber === 2)) {
            const defaultShiftTab: StationShiftTab = shiftNumber === 1 ? 'SHIFT_1' : 'SHIFT_2';
            setActiveShiftTab(defaultShiftTab);
            console.log(`StationsPageDB: Set default shift tab to ${defaultShiftTab} based on active shift ${shiftNumber}`);
          }
        }
      } catch (error) {
        console.log('StationsPageDB: Could not fetch current shift, using default tab');
      }
    };

    fetchCurrentShift();
    
    // Set up interval to check shift every 30 seconds
    const shiftInterval = setInterval(fetchCurrentShift, 30000);
    
    return () => clearInterval(shiftInterval);
  }, [isAppInitialized, apiUrl]);

  const handleUpdateAssignment = useCallback(async (updatedAssignment: StationAssignment) => {
    console.log('StationsPageDB: handleUpdateAssignment called with:', updatedAssignment);
    
    try {
      const requestBody = {
        lineId: updatedAssignment.lineId,
        stationNumber: updatedAssignment.stationNumber,
        assignedEmployeeId: updatedAssignment.assignedEmployeeId,
        status: updatedAssignment.status,
        stationStatus: updatedAssignment.stationStatus || 'active',
        shift: updatedAssignment.shift,
      };
      
      console.log('StationsPageDB: Making PUT request to:', `${apiUrl}/station-assignments/${updatedAssignment.id}`);
      console.log('StationsPageDB: Request body:', requestBody);

      // Save to database via API
      const response = await fetch(`${apiUrl}/station-assignments/${updatedAssignment.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      console.log('StationsPageDB: API response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('StationsPageDB: API error response:', errorText);
        
        // Try to parse error as JSON
        let errorMessage = `Failed to update station assignment: ${response.statusText}`;
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.error) {
            errorMessage = errorJson.error;
          }
        } catch (e) {
          // If not JSON, use the text as is
          errorMessage = errorText || errorMessage;
        }
        
        // Show user-friendly error alert
        alert(errorMessage);
        
        // Don't update local state if the server rejected the change
        return;
      }

      const responseData = await response.json();
      console.log('StationsPageDB: API response data:', responseData);

      // Update local state only after successful database save
      setStationAssignments(prev => {
        const updated = prev.map(sa => sa.id === updatedAssignment.id ? updatedAssignment : sa);
        console.log('StationsPageDB: Updated local state, total assignments:', updated.length);
        return updated;
      });
      
      console.log(`StationsPageDB: Successfully updated station assignment ${updatedAssignment.id} in database`);
    } catch (error) {
      console.error('StationsPageDB: Error updating station assignment:', error);
      // Don't update local state on error
      alert('Failed to update station assignment. Please try again.');
    }
  }, [apiUrl]);

  const activeLineStations = useMemo(() => {
    if (!activeLineId) return [];
    
    // Convert activeShiftTab to ShiftAssignment enum
    const currentShift = activeShiftTab === 'SHIFT_1' ? ShiftAssignment.SHIFT_1 : ShiftAssignment.SHIFT_2;
    
    return stationAssignments.filter(sa => 
      sa.lineId === activeLineId && sa.shift === currentShift
    ).sort((a, b) => a.stationNumber - b.stationNumber);
  }, [activeLineId, stationAssignments, activeShiftTab]);
  
  const activeLineDetails = useMemo(() => {
    return lines.find(l => l.id === activeLineId);
  }, [lines, activeLineId]);

  // Filter systems that should be visible on Stations page
  const visibleSystems = useMemo(() => {
    console.log('StationsPageDB: Filtering systems. Total systems:', systems.length);
    systems.forEach(system => {
      console.log(`StationsPageDB: System "${system.name}" - showOnStations:`, system.showOnStations, 'Type:', typeof system.showOnStations);
    });
    const filtered = systems.filter(system => Boolean(system.showOnStations));
    console.log('StationsPageDB: Filtered visible systems:', filtered.length);
    return filtered;
  }, [systems]);

  // Get the active system and its assigned lines
  const activeSystem = useMemo(() => {
    return visibleSystems.find(s => s.id === activeSystemId);
  }, [visibleSystems, activeSystemId]);

  const systemLines = useMemo(() => {
    if (!activeSystem || !activeSystem.lineIds) return [];
    return lines.filter(line => activeSystem.lineIds.includes(line.id));
  }, [activeSystem, lines]);

  // Auto-select line when system changes, preferring dashboard selected line
  useEffect(() => {
    if (systemLines.length > 0 && !activeLineId) {
      // Check if dashboard selected line is in this system
      const dashboardLineInSystem = selectedLineId && systemLines.find(line => line.id === selectedLineId);
      if (dashboardLineInSystem) {
        setActiveLineId(selectedLineId);
      } else {
        setActiveLineId(systemLines[0].id);
      }
    } else if (systemLines.length > 0 && activeLineId && !systemLines.find(line => line.id === activeLineId)) {
      // If current line is not in the new system, try dashboard selected line first
      const dashboardLineInSystem = selectedLineId && systemLines.find(line => line.id === selectedLineId);
      if (dashboardLineInSystem) {
        setActiveLineId(selectedLineId);
      } else {
        setActiveLineId(systemLines[0].id);
      }
    } else if (systemLines.length === 0) {
      setActiveLineId(null);
    }
  }, [systemLines, activeLineId, selectedLineId]);

  // Update employees with their current assignments whenever station assignments change
  useEffect(() => {
    if (employees.length === 0 || stationAssignments.length === 0 || lines.length === 0) {
      return;
    }

    console.log("StationsPageDB: Updating employee current assignments...");
    console.log("StationsPageDB: Total station assignments:", stationAssignments.length);
    console.log("StationsPageDB: Assignments with employees:", stationAssignments.filter(a => a.assignedEmployeeId).length);
    
    // Create a map of employee assignments
    const assignmentMap = new Map<string, any>();
    
    stationAssignments.forEach(assignment => {
      if (assignment.assignedEmployeeId) {
        const line = lines.find(l => l.id === assignment.lineId);
        if (line) {
          // If employee already has an assignment, keep the first one found
          if (!assignmentMap.has(assignment.assignedEmployeeId)) {
            assignmentMap.set(assignment.assignedEmployeeId, {
              lineId: assignment.lineId,
              lineName: line.name,
              stationNumber: assignment.stationNumber,
              shift: assignment.shift,
              status: assignment.status
            });
          }
          console.log(`StationsPageDB: Employee ${assignment.assignedEmployeeId} assigned to ${line.name} Station ${assignment.stationNumber} (${assignment.shift})`);
        }
      }
    });
    
    console.log("StationsPageDB: Assignment map size:", assignmentMap.size);
    
    // Update employees with their current assignments
    const updatedEmployees = employees.map(emp => {
      const assignment = assignmentMap.get(emp.id) || null;
      if (assignment) {
        console.log(`StationsPageDB: Setting assignment for employee ${emp.firstName} ${emp.lastName} (${emp.id}):`, assignment);
      }
      return {
        ...emp,
        currentAssignment: assignment
      };
    });
    
    // Only update if there are actual changes
    const hasChanges = updatedEmployees.some((emp, index) => {
      const oldAssignment = employees[index]?.currentAssignment;
      const newAssignment = emp.currentAssignment;
      return JSON.stringify(oldAssignment) !== JSON.stringify(newAssignment);
    });
    
    if (hasChanges) {
      console.log("StationsPageDB: Employee assignments updated. Employees with assignments:", 
                  updatedEmployees.filter(e => e.currentAssignment).length);
      setEmployees(updatedEmployees);
    } else {
      console.log("StationsPageDB: No changes detected in employee assignments");
    }
  }, [stationAssignments, lines]); // Don't include employees to avoid infinite loop

  // Filter employees based on selected shift
  const filteredEmployees = useMemo(() => {
    return employees.filter(emp => {
      // Include employees assigned to the specific shift OR employees assigned to both shifts
      return emp.shiftAssignment === activeShiftTab || emp.shiftAssignment === ShiftAssignment.BOTH_SHIFTS;
    });
  }, [employees, activeShiftTab]);

  const shift1Count = useMemo(() => {
    return employees.filter(emp => 
      emp.shiftAssignment === ShiftAssignment.SHIFT_1 || emp.shiftAssignment === ShiftAssignment.BOTH_SHIFTS
    ).length;
  }, [employees]);
  
  const shift2Count = useMemo(() => {
    return employees.filter(emp => 
      emp.shiftAssignment === ShiftAssignment.SHIFT_2 || emp.shiftAssignment === ShiftAssignment.BOTH_SHIFTS
    ).length;
  }, [employees]);


  const tabBaseStyle = "py-3 px-5 text-sm text-center cursor-pointer focus:outline-none whitespace-nowrap";
  const activeTabStyle = "border-b-2 border-indigo-600 text-indigo-600 font-bold";
  const inactiveTabStyle = "border-b-2 border-transparent text-slate-500 hover:text-indigo-600 hover:border-indigo-500 font-medium";
  
  // Determine if current shift matches the tab for additional styling
  const getCurrentShiftTabStyle = (tabShift: StationShiftTab) => {
    const isCurrentShift = currentShift === 1 && tabShift === 'SHIFT_1' || currentShift === 2 && tabShift === 'SHIFT_2';
    const isActiveTab = activeShiftTab === tabShift;
    
    if (isActiveTab && isCurrentShift) {
      return "border-b-2 border-indigo-600 text-indigo-600 font-bold";
    } else if (isActiveTab) {
      return "border-b-2 border-indigo-600 text-indigo-600 font-bold";
    } else if (isCurrentShift) {
      return "border-b-2 border-transparent text-slate-700 hover:text-indigo-600 hover:border-indigo-500 font-bold";
    } else {
      return "border-b-2 border-transparent text-slate-500 hover:text-indigo-600 hover:border-indigo-500 font-medium";
    }
  };

  // Show loading indicator if either the app is not initialized or the page is internally loading data
  if (!isAppInitialized || isLoading) {
    return (
      <div className="p-4 md:p-8 text-center">
        <h1 className="text-2xl md:text-3xl font-semibold text-slate-800 mb-6">Stations Configuration</h1>
        <p className="text-slate-600">Loading station data...</p>
      </div>
    );
  }

  if (activeLineDetails && activeLineStations.length > 0 && !isLoading) {
    console.log(`StationsPageDB: About to render StationRows for Line ${activeLineDetails.name}.`);
    console.log(`StationsPageDB: 'employees' state length: ${employees.length}. First employee:`, employees?.[0]);
    console.log(`StationsPageDB: 'activeLineStations' length: ${activeLineStations.length}. First station assignment:`, activeLineStations?.[0]);
  }


  return (
    <div className="p-2 md:p-4 space-y-3 min-h-screen bg-gradient-to-br from-gray-50 via-white to-indigo-50">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg shadow-md">
            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm3 1h6v4H7V5zm6 6H7v2h6v-2z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Stations Configuration
            </h1>
            <HelpIcon section="stations/viewing-stations" tooltip="Learn about station management" />
          </div>
        </div>
        <div className="flex items-center space-x-1 text-xs text-gray-500">
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
          </svg>
          <span>{new Date().toLocaleTimeString()}</span>
        </div>
      </div>

      {/* No Systems State */}
      {visibleSystems.length === 0 && (
         <div className="bg-white rounded-lg shadow-lg p-6 text-center border border-gray-100">
            <div className="w-12 h-12 mx-auto mb-3 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm3 1h6v4H7V5zm6 6H7v2h6v-2z" clipRule="evenodd" />
              </svg>
            </div>
            <p className="text-gray-700 text-base font-semibold mb-1">No Systems Available</p>
            <p className="text-gray-500 text-sm">Configure systems in Settings → System Configuration</p>
        </div>
      )}

      {/* System Level Tabs */}
      {visibleSystems.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-0.5">
          <nav className="flex space-x-0.5 overflow-x-auto" aria-label="System Tabs">
            {visibleSystems.map(system => (
              <button
                key={system.id}
                onClick={() => setActiveSystemId(system.id)}
                className={`flex-1 min-w-[100px] px-3 py-2 rounded-md font-medium transition-all duration-200 flex items-center justify-center space-x-1 text-sm ${
                  activeSystemId === system.id 
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md' 
                    : 'text-gray-600 hover:bg-gray-100 hover:text-indigo-600'
                }`}
                aria-current={activeSystemId === system.id ? 'page' : undefined}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                </svg>
                <span>{system.name}</span>
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* Active System Content */}
      {activeSystem && (
        <div>
          {/* No Lines in System State */}
          {systemLines.length === 0 && (
            <div className="bg-white rounded-lg shadow-md p-4 text-center border border-gray-100">
              <div className="w-10 h-10 mx-auto mb-2 bg-gradient-to-br from-yellow-100 to-orange-200 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-gray-700 text-sm font-semibold mb-1">No Lines in {activeSystem.name}</p>
              <p className="text-gray-500 text-xs">Assign lines in Settings → System Configuration</p>
            </div>
          )}

          {/* Line Level Tabs */}
          {systemLines.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-0.5 mt-2">
              <nav className="flex space-x-0.5 overflow-x-auto" aria-label="Line Tabs">
                {systemLines.map(line => (
                  <button
                    key={line.id}
                    onClick={() => setActiveLineId(line.id)}
                    className={`flex-1 min-w-[80px] px-3 py-1.5 rounded-md font-medium transition-all duration-200 flex items-center justify-center space-x-1 text-sm ${
                      activeLineId === line.id 
                        ? 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white shadow-md' 
                        : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                    }`}
                    aria-current={activeLineId === line.id ? 'page' : undefined}
                  >
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5z" />
                    </svg>
                    <span>{line.name}</span>
                  </button>
                ))}
              </nav>
            </div>
          )}
        </div>
      )}

      {/* Station Display Section */}
      {activeSystem && activeLineDetails && (
        <div className="mt-3">
          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white p-3 rounded-t-lg shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm3 1h6v4H7V5zm6 6H7v2h6v-2z" clipRule="evenodd" />
                </svg>
                <h2 className="text-lg font-bold">{activeLineDetails.name}</h2>
                <span className="ml-3 text-xs text-indigo-100">
                  {activeLineStations.length}/{activeLineDetails.numberOfStations} stations
                </span>
              </div>
              <div className="text-right">
                <span className="text-sm font-semibold">{activeLineDetails.numberOfStations} Total</span>
              </div>
            </div>
          </div>
          
          {/* Shift Tabs */}
          <div className="bg-white shadow-lg">
            <div className="flex">
              <button
                onClick={() => setActiveShiftTab('SHIFT_1')}
                className={`flex-1 px-4 py-3 font-semibold transition-all duration-200 flex items-center justify-center relative ${
                  activeShiftTab === 'SHIFT_1'
                    ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-inner'
                    : 'bg-gradient-to-b from-gray-50 to-gray-100 text-gray-600 hover:from-blue-50 hover:to-blue-100 hover:text-blue-700'
                }`}
                aria-current={activeShiftTab === 'SHIFT_1' ? 'page' : undefined}
              >
                {currentShift === 1 && (
                  <span className="absolute top-1 right-1">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400 border border-white"></span>
                    </span>
                  </span>
                )}
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 2a1 1 0 00-1 1v1a1 1 0 002 0V3a1 1 0 00-1-1zM4 4h3a3 3 0 006 0h3a2 2 0 012 2v9a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2zm2.5 7a1.5 1.5 0 100-3 1.5 1.5 0 000 3zm2.45 4a2.5 2.5 0 10-4.9 0h4.9zM12 9a1 1 0 100 2h3a1 1 0 100-2h-3zm-1 4a1 1 0 011-1h2a1 1 0 110 2h-2a1 1 0 01-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm">Shift 1</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                    activeShiftTab === 'SHIFT_1' ? 'bg-white/20 text-white' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {shift1Count}
                  </span>
                  {currentShift === 1 && (
                    <span className="text-xs font-medium text-green-300">●</span>
                  )}
                </div>
              </button>
              <button
                onClick={() => setActiveShiftTab('SHIFT_2')}
                className={`flex-1 px-4 py-3 font-semibold transition-all duration-200 flex items-center justify-center relative ${
                  activeShiftTab === 'SHIFT_2'
                    ? 'bg-gradient-to-r from-green-600 to-green-700 text-white shadow-inner'
                    : 'bg-gradient-to-b from-gray-50 to-gray-100 text-gray-600 hover:from-green-50 hover:to-green-100 hover:text-green-700'
                }`}
                aria-current={activeShiftTab === 'SHIFT_2' ? 'page' : undefined}
              >
                {currentShift === 2 && (
                  <span className="absolute top-1 right-1">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400 border border-white"></span>
                    </span>
                  </span>
                )}
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 2a1 1 0 00-1 1v1a1 1 0 002 0V3a1 1 0 00-1-1zM4 4h3a3 3 0 006 0h3a2 2 0 012 2v9a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2zm2.5 7a1.5 1.5 0 100-3 1.5 1.5 0 000 3zm2.45 4a2.5 2.5 0 10-4.9 0h4.9zM12 9a1 1 0 100 2h3a1 1 0 100-2h-3zm-1 4a1 1 0 011-1h2a1 1 0 110 2h-2a1 1 0 01-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm">Shift 2</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                    activeShiftTab === 'SHIFT_2' ? 'bg-white/20 text-white' : 'bg-green-100 text-green-700'
                  }`}>
                    {shift2Count}
                  </span>
                  {currentShift === 2 && (
                    <span className="text-xs font-medium text-green-300">●</span>
                  )}
                </div>
              </button>
            </div>
          </div>
          {(activeLineStations.length > 0 && !isLoading) ? ( 
            <div className="bg-white rounded-b-lg shadow-lg p-3 border border-gray-100">
               <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-center p-3 mb-3 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 rounded-lg font-semibold text-gray-700 text-xs border border-indigo-100">
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-2 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                    <path fillRule="evenodd" d="M4 5a2 2 0 012-2 1 1 0 000 2H6a2 2 0 00-2 2v6a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h.01a1 1 0 100-2H14a2 2 0 012 2v8a2 2 0 01-2 2H6a2 2 0 01-2-2V5z" clipRule="evenodd" />
                  </svg>
                  <span className="uppercase tracking-wide">Station</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-2 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                  <span className="uppercase tracking-wide">Employee</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  <span className="uppercase tracking-wide">Work Status</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="uppercase tracking-wide">Station Status</span>
                </div>
              </div>
              {activeLineStations.map(assignment => (
                <StationRow
                  key={`${assignment.id}-${assignment.assignedEmployeeId || 'unassigned'}-${assignment.status}`}
                  stationAssignment={assignment}
                  allEmployees={filteredEmployees} 
                  onUpdateAssignment={handleUpdateAssignment}
                  stationNumberDisplayName={`Station ${assignment.stationNumber}`}
                  allLineStationAssignments={activeLineStations}
                  lineMode={lineMode}
                  currentShift={currentShift}
                  currentLineId={activeLineId}
                />
              ))}
            </div>
          ) : (
             (activeLineDetails.numberOfStations > 0 && !isLoading) ?
             <div className="bg-white rounded-b-lg shadow-md p-4 text-center border border-gray-100">
               <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                 <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm3 1h6v4H7V5zm6 6H7v2h6v-2z" clipRule="evenodd" />
               </svg>
               <p className="text-gray-600 text-sm">No stations currently assigned.</p>
             </div>
             : (!isLoading && 
               <div className="bg-white rounded-b-lg shadow-md p-4 text-center border border-gray-100">
                 <svg className="w-8 h-8 text-yellow-400 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                   <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                 </svg>
                 <p className="text-gray-600 text-sm">No stations configured.</p>
               </div>)
          )}
        </div>
      )}
      {/* Helper Messages */}
      {activeSystem && systemLines.length > 0 && !activeLineId && (
        <div className="bg-white rounded-lg shadow-md p-4 text-center">
          <svg className="w-8 h-8 text-indigo-400 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <p className="text-gray-700 text-sm font-medium">Select a line to view its stations</p>
        </div>
      )}
    </div>
  );
};

export default StationsPageDB;
